import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface AdminDashboardCounts {
  total_users: number
  active_7d: number
  active_30d: number
  total_threads: number
  total_comments: number
  accessibility_listings: number
  broadcaster_interactions: number
  pending_reports_count: number
  new_users_7d: number
  new_users_prev_7d: number
  new_threads_7d: number
  new_threads_prev_7d: number
  new_comments_7d: number
  new_comments_prev_7d: number
  tv_listings_days: number
  last_sync_success_at: string | null
  last_sync_run_status: string | null
  last_sync_run_finished_at: string | null
}

const STALE_LISTINGS_DAYS = 2
const ENGAGEMENT_DROP_RATIO = 0.5
const REPORT_SPIKE_THRESHOLD = 5

export function useAdminDashboardCounts() {
  return useQuery({
    queryKey: ['admin-dashboard-counts'],
    queryFn: async (): Promise<AdminDashboardCounts | null> => {
      const { data, error } = await supabase.rpc('get_admin_dashboard_counts')
      if (error) throw error
      const row = Array.isArray(data) ? data[0] : data
      if (!row) return null
      return {
        total_users: Number(row.total_users ?? 0),
        active_7d: Number(row.active_7d ?? 0),
        active_30d: Number(row.active_30d ?? 0),
        total_threads: Number(row.total_threads ?? 0),
        total_comments: Number(row.total_comments ?? 0),
        accessibility_listings: Number(row.accessibility_listings ?? 0),
        broadcaster_interactions: Number(row.broadcaster_interactions ?? 0),
        pending_reports_count: Number(row.pending_reports_count ?? 0),
        new_users_7d: Number(row.new_users_7d ?? 0),
        new_users_prev_7d: Number(row.new_users_prev_7d ?? 0),
        new_threads_7d: Number(row.new_threads_7d ?? 0),
        new_threads_prev_7d: Number(row.new_threads_prev_7d ?? 0),
        new_comments_7d: Number(row.new_comments_7d ?? 0),
        new_comments_prev_7d: Number(row.new_comments_prev_7d ?? 0),
        tv_listings_days: Number(row.tv_listings_days ?? 7),
        last_sync_success_at: row.last_sync_success_at ?? null,
        last_sync_run_status: row.last_sync_run_status ?? null,
        last_sync_run_finished_at: row.last_sync_run_finished_at ?? null,
      }
    },
    staleTime: 60_000,
  })
}

export type AlertId = 'listings_stale' | 'sync_failed' | 'engagement_drop' | 'report_spike'

export interface DashboardAlert {
  id: AlertId
  label: string
  /** Screen reader / visible description */
  description: string
}

export function useDashboardAlerts(counts: AdminDashboardCounts | null): DashboardAlert[] {
  if (!counts) return []

  const alerts: DashboardAlert[] = []

  const lastSync = counts.last_sync_success_at
    ? new Date(counts.last_sync_success_at).getTime()
    : null
  const now = Date.now()
  const twoDaysAgo = now - STALE_LISTINGS_DAYS * 24 * 60 * 60 * 1000
  if (lastSync === null || lastSync < twoDaysAgo) {
    alerts.push({
      id: 'listings_stale',
      label: 'Listings data stale or missing',
      description:
        lastSync === null
          ? 'TV listings have not been successfully synced yet.'
          : 'Last successful TV listings sync was more than 2 days ago.',
    })
  }

  const status = (counts.last_sync_run_status ?? '').toLowerCase()
  if (
    counts.last_sync_run_finished_at &&
    status !== 'success' &&
    status !== ''
  ) {
    alerts.push({
      id: 'sync_failed',
      label: 'Last sync run failed',
      description: `Scheduled import last run status: ${counts.last_sync_run_status ?? 'failed'}.`,
    })
  }

  const threadDrop =
    counts.new_threads_prev_7d > 0 &&
    counts.new_threads_7d < ENGAGEMENT_DROP_RATIO * counts.new_threads_prev_7d
  const commentDrop =
    counts.new_comments_prev_7d > 0 &&
    counts.new_comments_7d < ENGAGEMENT_DROP_RATIO * counts.new_comments_prev_7d
  if (threadDrop || commentDrop) {
    alerts.push({
      id: 'engagement_drop',
      label: 'Engagement drop',
      description:
        'Thread or comment activity in the last 7 days is down more than 50% compared to the previous 7 days.',
    })
  }

  if (counts.pending_reports_count >= REPORT_SPIKE_THRESHOLD) {
    alerts.push({
      id: 'report_spike',
      label: 'Report queue high',
      description: `${counts.pending_reports_count} reports are pending review.`,
    })
  }

  return alerts
}
